/**
 * Search History Hook
 *
 * React binding over the framework-free history core in `searchCore/history`,
 * which is also used by the embeddable widget.
 */

import { useState, useCallback } from 'react';
import {
  HISTORY_STORAGE_KEY,
  addHistoryEntry,
  loadHistory,
  removeHistoryEntry,
  saveHistory,
} from '../searchCore/history';

export function useSearchHistory() {
  const [history, setHistory] = useState<string[]>(() => loadHistory(HISTORY_STORAGE_KEY));

  /** Add a query to the top of the history (deduplicates). */
  const addEntry = useCallback((query: string) => {
    setHistory((prev) => {
      const next = addHistoryEntry(prev, query);
      if (next !== prev) saveHistory(next, HISTORY_STORAGE_KEY);
      return next;
    });
  }, []);

  /** Remove a single entry from the history. */
  const removeEntry = useCallback((query: string) => {
    setHistory((prev) => {
      const next = removeHistoryEntry(prev, query);
      saveHistory(next, HISTORY_STORAGE_KEY);
      return next;
    });
  }, []);

  /** Clear all history entries. */
  const clearHistory = useCallback(() => {
    setHistory([]);
    saveHistory([], HISTORY_STORAGE_KEY);
  }, []);

  return { history, addEntry, removeEntry, clearHistory } as const;
}
